import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronUp, Pin, ArrowLeft, Sparkles } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";
import { RichView } from "@/components/rich/RichEditor";
import { TAGS, STAGES, imgUrl, fmtDate, SEEN_KEY } from "@/lib/siteNews";

const Chip = ({ label, color, testid }) => <span className="rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider" style={{ color, borderColor: `${color}55`, background: `${color}14` }} data-testid={testid}>{label}</span>;

const ComingCard = ({ item, user, i }) => {
  const qc = useQueryClient();
  const vote = async () => {
    try {
      const { data } = await api.post(`/site/roadmap/${item.id}/vote`);
      qc.setQueryData(["site-updates"], (d) => d && { ...d, coming: d.coming.map((c) => (c.id === item.id ? { ...c, ...data } : c)) });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const [label, color] = STAGES[item.stage] || STAGES.planned;
  return (
    <article className="lf-rise flex flex-col overflow-hidden rounded-md border border-[#2D3442] bg-[#13171F] transition-colors hover:border-[#D97706]/50" style={{ animationDelay: `${i * 60}ms` }} data-testid={`coming-card-${item.id}`}>
      {item.image_id && <img src={imgUrl(item.image_id)} alt="" className="aspect-[16/9] w-full object-cover" loading="lazy" />}
      <div className="flex flex-1 flex-col p-5">
        <div className="mb-3 flex flex-wrap items-center gap-2"><Chip label={label} color={color} testid={`coming-stage-${item.id}`} />{item.eta && <span className="text-[11px] text-[#A8A090]" data-testid={`coming-eta-${item.id}`}>Expected {item.eta}</span>}</div>
        <h3 className="font-display text-2xl leading-tight text-[#F3EBDD]">{item.title}</h3>
        {item.description && <RichView markdown={item.description} className="mt-2 text-sm text-[#A8A090]" testid={`coming-desc-${item.id}`} />}
        <div className="mt-auto pt-4">
          {user ? (
            <button onClick={vote} className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors ${item.voted ? "border-[#D97706] bg-[#D97706]/15 text-[#FBBF24]" : "border-[#2D3442] text-[#E8DFD1] hover:border-[#D97706]/60"}`} data-testid={`coming-vote-${item.id}`}>
              <ChevronUp className="h-3.5 w-3.5" />{item.voted ? "You want this" : "I want this"}<span className="text-[#A8A090]" data-testid={`coming-votes-${item.id}`}>· {item.votes || 0}</span>
            </button>
          ) : <Link to="/login" className="text-xs text-[#F59E0B] hover:underline" data-testid={`coming-login-${item.id}`}>Sign in to vote · {item.votes || 0}</Link>}
        </div>
      </div>
    </article>
  );
};

const Post = ({ p }) => {
  const [label, color] = TAGS[p.tag] || TAGS.update;
  return (
    <article id={`post-${p.id}`} className="scroll-mt-24 overflow-hidden rounded-md border border-[#2D3442] bg-[#13171F]" data-testid={`news-post-${p.id}`}>
      {p.cover_id && <img src={imgUrl(p.cover_id)} alt="" className="max-h-[380px] w-full object-cover" loading="lazy" />}
      <div className="p-6 sm:p-8">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {p.pinned && <span className="flex items-center gap-1 text-[11px] text-[#F59E0B]" data-testid={`news-pinned-${p.id}`}><Pin className="h-3 w-3" />Pinned</span>}
          <Chip label={label} color={color} testid={`news-tag-${p.id}`} />
          <span className="text-xs text-[#A8A090]">{fmtDate(p.published_at)}</span>
        </div>
        <h3 className="font-display text-3xl leading-tight text-[#F3EBDD]" data-testid={`news-title-${p.id}`}>{p.title}</h3>
        {p.body && <RichView markdown={p.body} images className="mt-4 text-[15px]" testid={`news-body-${p.id}`} />}
      </div>
    </article>
  );
};

export default function UpdatesPage() {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({ queryKey: ["site-updates"], queryFn: () => api.get("/site/updates").then((r) => r.data) });
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!data || !id) return undefined;
    const go = () => document.getElementById(id)?.scrollIntoView({ block: "start" });
    go();
    const timers = [300, 900, 1800].map((ms) => setTimeout(go, ms));
    return () => timers.forEach(clearTimeout);
  }, [data]);
  useEffect(() => {
    const latest = data?.posts?.reduce((m, p) => (p.published_at > m ? p.published_at : m), "");
    if (latest) { localStorage.setItem(SEEN_KEY, latest); window.dispatchEvent(new Event("lf-news-seen")); }
  }, [data]);
  return (
    <div className="min-h-screen lf-glow lf-grain" data-testid="updates-page">
      <div className="relative z-10 mx-auto max-w-6xl px-6 py-10 sm:px-10">
        <header className="mb-16 flex items-center justify-between gap-3">
          <Link to="/"><BrandLogo testId="updates-brand" /></Link>
          <Link to={user ? "/" : "/login"} className="flex items-center gap-1 text-sm text-[#A8A090] hover:text-[#F3EBDD]" data-testid="updates-back"><ArrowLeft className="h-4 w-4" />{user ? "My universes" : "Sign in"}</Link>
        </header>
        <section className="max-w-3xl">
          <div className="lf-label mb-3 flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" />Always in progress</div>
          <h1 className="font-display text-4xl text-[#F3EBDD] sm:text-5xl lg:text-6xl">Building LoreRight</h1>
          <p className="mt-5 whitespace-pre-line text-base text-[#A8A090] md:text-lg" data-testid="updates-intro">{data?.intro}</p>
        </section>
        {isLoading && <p className="mt-16 text-sm text-[#A8A090]">Loading…</p>}
        {data?.coming?.length > 0 && (
          <section className="mt-20" data-testid="coming-soon-section">
            <h2 className="mb-6 font-display text-3xl text-[#F3EBDD]">Coming soon</h2>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{data.coming.map((c, i) => <ComingCard key={c.id} item={c} user={user} i={i} />)}</div>
          </section>
        )}
        <section className="mt-20 max-w-3xl" data-testid="news-section">
          <h2 className="mb-6 font-display text-3xl text-[#F3EBDD]">News & updates</h2>
          {data && !data.posts.length && <p className="text-sm text-[#A8A090]" data-testid="news-empty">No news yet. Check back soon.</p>}
          <div className="space-y-8">{data?.posts?.map((p) => <Post key={p.id} p={p} />)}</div>
        </section>
      </div>
    </div>
  );
}
